import styled from 'styled-components'
import { accents, gray } from 'sutra/tokens/colors'

export const CardRoot = styled.div<{
  $disabled: boolean
  // Ownership of this card's filter: 'local' = the tile's own (orange bar),
  // 'global' = inherited from a parent scope (navy bar). Undefined = no bar
  // at all — every non-dashboard caller (charts, feedback, keywords).
  // The bar itself is rendered by `FilterCardAccentBar`, not this component —
  // it needs to be a real element (not a `::before`) so it can host a hover
  // tooltip. This prop only reserves the padding the bar sits inside.
  $accent?: 'local' | 'global'
  $superseded?: boolean
}>`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 6px 12px;
  padding-left: ${({ $accent }) => ($accent ? '20px' : '12px')};
  background-color: ${({ $disabled, $superseded }) =>
    $disabled || $superseded ? gray[100] : '#ffffff'};
  position: relative;
`

// Reveals its content only while the card (CardRoot) is hovered — shared by
// the always-mounted-but-invisible hover affordances (the inherited-filter
// lock badge, EditableTextFilterCard's remove/reset actions) so a card's
// hover chrome behaves consistently across every filter card kind.
export const RevealOnCardHover = styled.div`
  display: inline-flex;
  align-items: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;

  ${CardRoot}:hover & {
    opacity: 1;
    pointer-events: auto;
  }
`

export const CardTitleRow = styled.div<{
  $disabled: boolean
  $superseded?: boolean
}>`
  display: flex;
  align-items: center;
  text-decoration: ${({ $disabled, $superseded }) =>
    $disabled || $superseded ? 'line-through' : 'none'};
`

// The muted `Group > ` that leads a card's title when "Show breakdown
// hierarchy" is on.
export const GroupPrefix = styled.span`
  color: #9ca3af;
  font-weight: 400;
`

export const CardBody = styled.div<{
  $disabled: boolean
  $superseded?: boolean
}>`
  display: flex;
  align-items: center;
  font-size: 14px;
  gap: 2px;
  text-decoration: ${({ $disabled, $superseded }) =>
    $disabled || $superseded ? 'line-through' : 'none'};
`

export const CardActions = styled.div`
  display: flex;
  gap: 4px;
`

// A clickable inline label inside a card body — operator, value, mode,
// rating-type, breakdown/metric name. Highlights on hover (CSS `:hover`) or
// while its owned popover is open (`$open`, real state — not a pointer
// position). Every card previously tracked an `isXHovered` useState just to
// compute this same background-color; the hover half moves to CSS here, so
// only the popover-open half stays as a prop.
export const FilterChip = styled.span<{
  $open?: boolean
  $disabled?: boolean
  // Operator chips render grey (#6b7280); value/mode/type chips render dark
  // (#374151, the default) — the same distinction the static "is"/"and"
  // labels next to them already carry.
  $color?: string
  // Keeps a multi-word label ("is not") on one line when a sibling chip that
  // fills the row (`width: 100%`) would otherwise squeeze it to one word.
  $nowrap?: boolean
}>`
  color: ${({ $color = '#374151' }) => $color};
  ${({ $nowrap }) => $nowrap && 'white-space: nowrap; flex-shrink: 0;'}
  padding: 4px 8px;
  border-radius: 4px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  transition: all 0.2s ease;
  font-size: 12px;
  -webkit-user-select: none;
  user-select: none;
  background-color: ${({ $open }) =>
    $open ? accents.reflectionBlue[100] : 'transparent'};
  ${({ $disabled }) =>
    !$disabled &&
    `&:hover {
      background-color: ${accents.reflectionBlue[100]};
    }`}
`

export const CardActionButton = styled.button<{ $variant: 'danger' | 'muted' }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: none;
  background-color: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ $variant }) =>
      $variant === 'danger' ? '#ef4444' : gray[200]};
    color: ${({ $variant }) => ($variant === 'danger' ? '#ffffff' : gray[700])};
  }
`
